import React, { useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from './store/slices/session';
import CachingController from './CachingController';
import { generateClientUrl, parseUrl } from './utils/urlParser';
import Loader from './components/portals/Loader';
import { useSnackbar } from 'notistack';
import { removeCompanyReferer, setCompanyReferer } from './utils/cookieUtils';
import { setNavigate } from './utils/navigationUtils';
import { useGetAllCompaniesMutation } from './api/company';
import { useGetCurrentUserMutation } from './api/account';
import { setLoading } from './store/slices/app';
import { loadFreshdeskScripts } from './utils/freshdesk';
import { getSidebarItems, getSideBarMenuRoutes } from './components/layout/SideBarMenuRoutes';

const App = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.session);
    const { isLoading } = useSelector((state) => state.app);
    const { enqueueSnackbar } = useSnackbar();

    const [getAllCompanies, { isLoading: isCompanyListLoading }] = useGetAllCompaniesMutation();
    const [getCurrentUserApi, { isLoading: isCurrentUserLoading }] = useGetCurrentUserMutation();

    useEffect(() => {
        setNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        const getCurrentUser = async () => {
            const urlInfo = parseUrl();
            const { environment, isDefaultUrl, clientName } = urlInfo;
            let companyList;

            try {
                dispatch(setLoading(true));
                if (isDefaultUrl) {
                    // Parse current URL to determine environment and client
                    const companyResData = await getAllCompanies().unwrap();
                    if (!companyResData) return;
                    if (companyResData.status === 'ERROR') throw new Error(companyResData?.message);

                    companyList = companyResData?.companies;
                    if (companyList?.length <= 0) {
                        enqueueSnackbar('Company not assigned', { variant: 'error' });
                        navigate('/login');
                        return;
                    }

                    // If single company, set it as default
                    if (companyList?.length === 1) setCompanyReferer(companyList[0]?.slug);
                    else removeCompanyReferer();
                } else {
                    setCompanyReferer(clientName);
                }

                const response = await getCurrentUserApi().unwrap();
                if (!response) {
                    dispatch(updateUser({}));
                    navigate('/login');
                    return;
                }
                if (response.status === 'ERROR') throw new Error(response?.message);

                dispatch(updateUser(response));

                if (isDefaultUrl) {
                    // Default URL logic
                    if (companyList?.length === 1) {
                        const targetUrl = generateClientUrl(environment, companyList[0]?.slug, '/');
                        window.location.href = targetUrl;
                    } else {
                        // Multiple companies - redirect to company selection
                        navigate('/companySelect');
                    }
                } else {
                    if (location.pathname === '/') {
                        const authorities = response.authorities;
                        const isClientHqEnabled = response.isClientHqEnabled;

                        const sideMarItems = getSidebarItems(
                            authorities,
                            { isClientHqEnabled: isClientHqEnabled },
                            getSideBarMenuRoutes({ isClientHqEnabled: isClientHqEnabled }),
                        );

                        navigate(sideMarItems[0]?.href || '/account');
                    }
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong', { variant: 'error' });
                dispatch(updateUser({}));
                navigate('/login');
            } finally {
                dispatch(setLoading(false));
            }
        };
        getCurrentUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!user?.userDBId) return;
        let cleanup = loadFreshdeskScripts({ name: user.fullName || '', email: user?.emailAddresses?.[0] || '' });
        return cleanup;
    }, [user]);

    return (
        <React.Fragment>
            {(isLoading || !user?.userDBId) && <Loader />}
            {user?.userDBId && (
                <React.Fragment>
                    <CachingController />
                    <Outlet />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default App;
